// Pure finance maths shared by the worker reports route and the client
// (GST splits in the modals, invoice-number defaulting). Kept here so the
// BAS quarter logic is unit-testable without a D1 binding.

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Default GST component for an inc-GST amount: amount / 11, rounded (AU). */
export function gstFromGross(amountCents: number): number {
  return Math.round(amountCents / 11);
}

// Henry quotes ex GST (2026-06-13): lead/project values are ex-GST figures,
// the new-invoice modal accepts ex-GST entry, and convert derives the draft
// invoice's inc-GST amount from the lead value. Storage stays inc-GST.

/** GST added on top of an ex-GST amount (AU 10%). */
export function gstOnEx(exCents: number): number {
  return Math.round(exCents / 10);
}

/** Inc-GST gross for an ex-GST amount: ex + 10%. */
export function grossFromEx(exCents: number): number {
  return exCents + gstOnEx(exCents);
}

/** Ex-GST component of a stored inc-GST amount. */
export function exGst(amountCents: number, gstCents: number): number {
  return amountCents - gstCents;
}

/** Australian-FY quarter for an ISO date (Q1 = Jul–Sep; FY named for its
    end year: Jul 2025 → FY26 Q1). Moved verbatim from the reports route. */
export function quarterOf(iso: string): { key: string; start: string } | null {
  if (!DATE_RE.test(iso)) return null;
  const year = Number(iso.slice(0, 4));
  const month = Number(iso.slice(5, 7)); // 1-12
  const fyEndYear = month >= 7 ? year + 1 : year;
  const q = month >= 7 ? Math.ceil((month - 6) / 3) : Math.ceil(month / 3) + 2;
  const startMonth = [7, 10, 1, 4][q - 1]!;
  const startYear = startMonth >= 7 ? fyEndYear - 1 : fyEndYear;
  return {
    key: `FY${String(fyEndYear).slice(2)} Q${q}`,
    start: `${startYear}-${String(startMonth).padStart(2, '0')}-01`,
  };
}

/** Default invoice number for a project: the project's number for its first
    invoice, then "-2", "-3" suffixes for additional ones (Henry's call).
    Stored unpadded ("231", "231-2"); display pads via formatInvoiceNumber.
    Returns null when the project has no number yet. */
export function deriveInvoiceNumber(
  projectNumber: number | null | undefined,
  existingNumbers: readonly string[],
): string | null {
  if (projectNumber === null || projectNumber === undefined) return null;
  const base = String(projectNumber);
  // Count invoices already in this project's sequence ("231", "231-2", …),
  // tolerating zero-padded storage just in case.
  const re = new RegExp(`^0*${base}(?:-(\\d+))?$`);
  let highest = 0;
  for (const n of existingNumbers) {
    const m = re.exec(n.trim());
    if (!m) continue;
    highest = Math.max(highest, m[1] ? Number(m[1]) : 1);
  }
  return highest === 0 ? base : `${base}-${highest + 1}`;
}

/** Display status: a `sent` invoice past its due date reads as `overdue`
    everywhere Henry looks at it — the stored `status` column only flips via
    the daily overdue-flag pass, so callers recompute it live. Mirrors
    client/pages/Finance.tsx's `displayStatus`, moved here (Wave C2) so
    worker code — which can't import a client page — shares the exact same
    rule for the public invoice page (routes/publicInvoice.ts). */
export function effectiveInvoiceStatus<S extends string>(
  status: S,
  dueDate: string | null,
  today: string,
): S | 'overdue' {
  if ((status as string) === 'sent' && dueDate !== null && dueDate < today) return 'overdue';
  return status;
}

/** Public invoice-page visibility gate (Fix 0.4, 2026-07). A tokenized public
    invoice page must never expose a `void` invoice, nor a `draft` (not-yet-
    issued) invoice that a stranger with the link would read as a real payable
    invoice — with ONE exception: deposit invoices from the proposal-signed
    flow are deliberately created as `draft` and ARE meant to be paid via this
    page. `isDeposit` means the invoice is referenced by a
    `project_artifacts.deposit_invoice_id` (the reliable marker — a deposit
    raised while Stripe was unconfigured has no payment link but is still
    payable here). When this returns false the route returns the SAME not-found
    page a bad token gets — no information leak about the draft's existence. */
export function isPublicInvoiceViewable(effectiveStatus: string, isDeposit: boolean): boolean {
  if (effectiveStatus === 'void') return false;
  if (effectiveStatus === 'draft') return isDeposit;
  return true;
}
